import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import SEO from '../../common/SEO';
import GoogleMaps from '../../components/GoogleMaps';
import ServicesNavigationFooter from '../../components/ServicesNavigationFooter';
import RelatedTopics from '../../components/RelatedTopics';
import * as FiIcons from 'react-icons/fi';

const { 
  FiArrowLeft, FiCheckCircle, FiShield, FiCloud, 
  FiThermometer, FiMapPin, FiCheck, FiInfo, FiHelpCircle, FiHome 
} = FiIcons;

function SheepWoolInsulation() {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Schafswolle Dämmung: Natürlich & Nachhaltig | MK-AUSBAU",
    "alternativeHeadline": "Der ökologische Dämmstoff aus der Natur",
    "image": "https://mx.ausbau.nrw/img/Daemmtechnik_xl.jpg",
    "author": {
      "@type": "Person",
      "name": "Maxim Kelm",
      "jobTitle": "Geschäftsführer MK-AUSBAU",
      "url": "https://ausbau.nrw/ueber"
    },
    "publisher": {
      "@type": "Organization",
      "name": "MK-AUSBAU",
      "logo": {
        "@type": "ImageObject",
        "url": "https://ausbau.nrw/logo-icon.svg"
      }
    },
    "datePublished": "2026-02-15",
    "description": "Schafswolle als Dämmung? Erfahren Sie alles über den natürlichen Dämmstoff, seine Herkunft und Einsatzgebiete im Hausbau.",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://ausbau.nrw/blog/schafwolle-daemmung"
    }
  };

  return (
    <div className="py-20 bg-white">
      <SEO 
        title="Schafswolle Dämmung: Natürlich & Gesund | MK-AUSBAU"
        description="Alles über Schafwolle als Dämmstoff. Einsatzgebiete, Herkunft und warum sie für gesundes Wohnen ideal ist. 🌿 Jetzt lesen!"
        keywords="Schafwolle Dämmung, ökologische Dämmung, Naturdämmstoff, Schafwolle bauen, Dämmung NRW"
        schema={articleSchema}
        canonical="/blog/schafwolle-daemmung"
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/blog" className="inline-flex items-center text-mk-teal-600 hover:text-mk-teal-700 font-bold mb-8 transition-colors group">
          <SafeIcon icon={FiArrowLeft} className="mr-2 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Zurück zur Übersicht
        </Link>

        <motion.article 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="prose prose-lg prose-mk max-w-none"
        >
          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center space-x-3 text-mk-teal-600 font-bold uppercase tracking-wider text-sm mb-4">
              <SafeIcon icon={FiCloud} />
              <span>Ökologische Baustoffe</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
              🐑 Schafswolle Dämmung: Der natürliche Alleskönner
            </h1>
            <div className="flex items-center space-x-4 text-gray-500 text-sm border-y border-gray-100 py-4">
              <span className="flex items-center"><SafeIcon icon={FiShield} className="mr-1" /> Basis-Wissen</span>
              <span className="flex items-center"><SafeIcon icon={FiInfo} className="mr-1" /> MK-AUSBAU</span>
            </div>
          </header>

          <div className="lead text-xl text-gray-600 leading-relaxed mb-12">
            Schafwolle ist einer der ältesten und faszinierendsten Dämmstoffe der Menschheit. Was Kleidung wärmt, schützt auch Häuser. In diesem Artikel geben wir einen Überblick über das Material und seine besonderen Eigenschaften.
          </div>

          {/* Content */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">Was ist Schafwolldämmung?</h2>
          <p className="text-gray-700 mb-6">
            Schafwolldämmung besteht zu 100% aus gewaschener Schurwolle, die meist mit einem Stützgitter (oft auch organisch) zu Vliesen oder Matten verarbeitet wird. Sie ist der einzige Dämmstoff, der von Natur aus für Wärme- und Kälteschutz am lebenden Objekt "entwickelt" wurde.
          </p>

          <div className="bg-mk-teal-50 p-6 rounded-xl border-l-4 border-mk-teal-500 my-8">
            <h3 className="text-mk-teal-900 font-bold mt-0 mb-2">Wussten Sie schon?</h3>
            <p className="text-mk-teal-800 m-0">
              Ein Schaf produziert pro Jahr etwa 3-5 kg Wolle. Für ein Einfamilienhaus benötigt man die Schur von ca. 300 Schafen. Das stärkt die regionale Landwirtschaft und Landschaftspflege.
            </p>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">Einsatzgebiete</h2>
          <ul className="space-y-3 mb-8">
            <li className="flex items-start"><SafeIcon icon={FiCheck} className="text-mk-teal-500 mr-2 mt-1"/> <strong>Dachschrägen:</strong> Zwischensparrendämmung</li>
            <li className="flex items-start"><SafeIcon icon={FiCheck} className="text-mk-teal-500 mr-2 mt-1"/> <strong>Innendämmung:</strong> Ideal bei Fachwerkhäusern</li>
            <li className="flex items-start"><SafeIcon icon={FiCheck} className="text-mk-teal-500 mr-2 mt-1"/> <strong>Trittschall:</strong> Als Filz unter Böden</li>
            <li className="flex items-start"><SafeIcon icon={FiCheck} className="text-mk-teal-500 mr-2 mt-1"/> <strong>Stopfwolle:</strong> Für Fensterrahmen und Fugen</li>
          </ul>

          <div className="bg-white border-2 border-gray-100 p-8 rounded-2xl text-center shadow-lg my-12">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Sie wollen tiefer einsteigen?</h3>
            <p className="text-gray-600 mb-6">
              Möchten Sie wissen, ob sich der Mehrpreis lohnt und welche Nachteile es gibt? Lesen Sie unseren detaillierten Vergleich.
            </p>
            <Link 
              to="/blog/schafwolle-daemmung-vorteile-nachteile" 
              className="inline-flex items-center text-mk-teal-600 font-bold hover:text-mk-teal-800 transition-colors"
            >
              Zum Artikel: Vorteile & Nachteile <SafeIcon icon={FiArrowLeft} className="ml-2 rotate-180" />
            </Link>
          </div>

        </motion.article>
      </div>
      
      <RelatedTopics />
      <ServicesNavigationFooter />
      <GoogleMaps />
    </div>
  );
}

export default SheepWoolInsulation;